import { FC } from 'react';
import { cn } from '@/shared/lib';
import { useTranslation } from 'react-i18next';
import { Checkbox, Input, Textarea } from '@/shared/shadcn';
import { useInputsChangePersonalInfo } from '@/features/hooks';
import { InputErrorMessage, PhoneInputComponent, Typography } from '@/shared/components';
import { setCommentary, setNameError, setPhoneError, useAppDispatch, useAppSelector } from '@/app/store';

interface BasketInputsProps {
  isTakeout?: boolean;
  commentsEnabled: boolean;
  isOrderAnotherPerson?: boolean;
}

export const BasketInputs: FC<BasketInputsProps> = ({ commentsEnabled, isTakeout = false, isOrderAnotherPerson }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('address');
  const { nameError, phoneError } = useAppSelector((state) => state.errors);
  const { noCallIsNeededOptionEnabled } = useAppSelector((state) => state.initial.settings);
  const { name, phone, commentary, noCallNeeded } = useAppSelector((state) => state.order);
  const {
    name: anotherName,
    phone: anotherPhone,
    noCallNeeded: anotherNoCallNeeded,
  } = useAppSelector((state) => state.orderAnotherPerson);

  const { onNameChange, onPhoneChange, onNoCallNeededChange } = useInputsChangePersonalInfo(isOrderAnotherPerson);

  return (
    <>
      <div>
        <Input
          value={isOrderAnotherPerson ? anotherName : name}
          placeholder={t('placeholders.name')}
          onChange={(e) => {
            onNameChange(e.target.value);
            dispatch(setNameError(undefined));
          }}
          className={cn(nameError && 'border-destructive')}
        />

        {nameError && <InputErrorMessage text={nameError} />}
      </div>

      <div className='z-[1]'>
        <PhoneInputComponent
          value={isOrderAnotherPerson ? anotherPhone : phone}
          error={!!phoneError}
          onChange={(value, data) => {
            dispatch(setPhoneError(undefined));
            const digitsOnly = value.replace(/\D/g, '');
            // Если осталось только dialCode → считаем, что поле пустое
            if (digitsOnly.length <= data.dialCode.length) onPhoneChange('');
            else onPhoneChange(`+${value}`);
          }}
        />

        {phoneError && <InputErrorMessage text={phoneError} />}

        {noCallIsNeededOptionEnabled && (
          <div
            onClick={() => onNoCallNeededChange(isOrderAnotherPerson ? !anotherNoCallNeeded : !noCallNeeded)}
            className={cn('flex w-fit mt-1.5 gap-2 h-4 cursor-pointer')}
          >
            <Checkbox checked={isOrderAnotherPerson ? anotherNoCallNeeded : noCallNeeded} className='size-4' />
            <Typography variant='subtitle2'>{t('info.noNeedCall')}</Typography>
          </div>
        )}
      </div>

      {commentsEnabled && (
        <Textarea
          maxLength={512}
          value={commentary}
          onChange={(e) => dispatch(setCommentary(e.target.value))}
          placeholder={isTakeout ? t('placeholders.comment') : t('placeholders.commentCourier')}
          className={cn('resize-none h-[69px]')}
        />
      )}
    </>
  );
};
